<template>
  <layout-default>
    <template v-slot:toolbar>
      <v-spacer />
      <v-fade-transition>
        <div v-if="quickEditProgress" class="sequence__quick-edit-progress">
          <v-fade-transition>
            <span class="mr-4" v-if="quickEditProgressValue > 100"
              >Saving...</span
            >
          </v-fade-transition>
          <v-progress-circular
            color="accent"
            :indeterminate="quickEditProgressValue > 100 ? true : false"
            :value="quickEditProgressValue"
          ></v-progress-circular>
        </div>
      </v-fade-transition>
    </template>
    <div id="sequence" class="v-inner--wrap">
      <preview-zoom-dialog
        :dialogType="dialogType"
        :imageSrc="imageSrc"
        :dialogFile="dialogFile"
        :dialogFileMlUrl="dialogFileMlUrl"
        :name="imageName"
        @close-dialog="closeMediaDialog"
        @reload-resource="reloadImageResource"
      />
      <v-overlay :value="loading" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <sequence-exercises-container
        ref="sequenceExercisesContainer"
        :days="newSequenceData.days"
        @new-exercise="prepareNewExercise"
        @edit-exercise="prepareEditExercise"
        @quick-edit="prepareQuickEdit"
        @series-quick-edit="prepareSeriesQuickEdit"
        @delete-exercise="prepareDeleteExercise"
        @copy-exercise="copySequenceExercise"
        @copy-exercises="prepareCopySequenceExercises"
        @paste-exercises="pasteSquenceExercises"
        @move-exercise="prepareMoveExercise"
        @remove-exercise="prepareRemoveExercise"
        @remove-exercises="prepareRemoveExercises"
        @duplicate-exercise="prepareDuplicateExercise"
        @show-info-dialog="openInfoDialog"
      />
      <sequence-copy-exercises-dialog
        :show-dialog="showSequenceCopyExercisesDialog"
        :sequenceCopyExercisesData="sequenceCopyExercisesData"
        @close-exercise-dialog="closeSequenceCopyExercisesDialog"
        @on-submit="submitCopySequenceExercises"
      />
      <bottom-right-info-dialog
        :show-dialog="infoDialog"
        title="Hint"
        @submit="closeInfoDialog"
      >
        <p class="mb-1">You can drag single exercise between days.</p>
        <p class="mb-1">Click on exercise to select it.</p>
        <p class="mb-1">Drag to select items.</p>
        <p class="mb-1">
          Hold {{ isApple ? '⌘ CMD' : 'CTRL' }} key and click to add a item to
          selection.
        </p>
        <p class="mb-1">
          Hold {{ isApple ? '⌘ CMD' : 'CTRL' }} key and drag to add multiple
          items to selection.
        </p>
      </bottom-right-info-dialog>

      <sequence-exercise-dialog
        :show-dialog="showNewExerciseDialog"
        :key="'new-' + newExerciseDialogKey"
        :exerciseDefinitionsLoading="sequenceLoading"
        :day="newExerciseDay"
        :get-resource="getResource"
        :get-mime-types="getMimeTypes"
        :exercise-resource="exerciseResource ? exerciseResource : false"
        :resource="resource ? resource : false"
        :edited-exercise="exerciseToEdit"
        :exercise-definitions="exerciseDefinitions"
        :create-new-exercise-definition="true"
        :exercise-definitions-pagination="exerciseDefinitionsPagination"
        @show-create-new-exercise-dialog="openCreateNewExerciseDialog"
        @close-exercise-dialog="closeNewExerciseDialog"
        @on-submit="addSequenceExercise"
        @on-edit="editSequenceExercise"
        @on-choose-existing="addSequenceExistingExercise"
        @reload-resource="reloadExerciseResource"
        @clear-resource="clearExerciseResource"
        @show-bigger-image="showBiggerImage"
        @reload-exercises="reloadExerciseDefinitions"
        @search-clear="searchClear"
        @search-on:input="searchInput"
        @search-on:multiple="searchMultiple"
        @search-on:run="searchRun"
        @preview-exercise-definition="exerciseGetPreview"
      />
      <exercise-definition-dialog
        :show-dialog="showEditExerciseDialog"
        :key="'edit-' + editExerciseDialogKey"
        :edited-exercise="exerciseDefinition"
        :add-alternatives="false"
        :enabled-notes="true"
        :participants-exercises="true"
        :get-resource="getResource"
        :get-mime-types="getMimeTypes"
        :exercise-resource="exerciseResource ? exerciseResource : false"
        :exercise-resource-additional="
          exerciseResourceAdditional ? exerciseResourceAdditional : false
        "
        :exercise-definitions="exerciseSingleDefinitions"
        :add-to-series="exerciseDefinition ? false : true"
        :parameters-enabled="true"
        :hide-prev="true"
        :repetition-options-enabled="false"
        :wait-for-submit-response="true"
        @close-exercise-dialog="
          showEditExerciseDialog = false
          editExerciseDialogKey++
        "
        @show-bigger-image="showBiggerImage"
        @on-submit="addSequenceExercise"
        @on-edit="saveEditExercise"
        @reload-resource="reloadExerciseResource"
        @reload-exercise-resource="reloadExerciseResource"
        @reload-exercise-resource-additional="
          reloadExerciseResource($event, 'additionalMedia')
        "
        @clear-resource="clearExerciseResource"
        @clear-resource-additional="clearExerciseResourceAdditional"
        @on-series-search="loadDialogExerciseDefinitions"
      />
      <preview-exercise-dialog
        :show-dialog="showExercisePreviewDialog"
        @close-dialog="showExercisePreviewDialog = false"
        @preview-media="openMediaPreview"
        :exercise="exerciseDefinition"
      />
      <v-dialog
        v-model="deleteExercisePopup"
        persistent
        max-width="290"
        @click:outside="onClickOutside"
        @keydown="onKeydown"
      >
        <v-card>
          <v-card-title class="headline">Delete this exercise?</v-card-title>
          <v-card-text>
            This action is irreversible and will affect all routines using this
            exercise.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="rounded-pill" text @click="closeDeleteExercisePopup"
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="deleteSequenceExercise"
              >Delete</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </layout-default>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import InnerContent from '@/components/InnerContent.vue'
import SequenceExercisesContainer from '@/components/exercises/SequenceExercisesContainer.vue'
import SequenceExerciseDialog from '@/components/dialogs/SequenceExerciseDialog.vue'
import LayoutDefault from '../../layouts/LayoutDefault.vue'
import PreviewExerciseDialog from '@/components/dialogs/PreviewExerciseDialog.vue'
import PreviewZoomDialog from '@/components/dialogs/PreviewZoomDialog.vue'
import ExerciseDefinitionDialog from '@/components/dialogs/ExerciseDefinitionDialog.vue'
import SequenceCopyExercisesDialog from '@/components/dialogs/SequenceCopyExercisesDialog.vue'
import BottomRightInfoDialog from '@/components/dialogs/BottomRightInfoDialog.vue'
import { debounce } from '@/helpers/debounce'
import { isApple } from '@/helpers/isApple'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'Sequence',
  components: {
    LayoutDefault,
    SequenceExercisesContainer,
    SequenceExerciseDialog,
    PreviewZoomDialog,
    PreviewExerciseDialog,
    ExerciseDefinitionDialog,
    SequenceCopyExercisesDialog,
    BottomRightInfoDialog,
  },
  data() {
    return {
      showSequenceCopyExercisesDialog: false,
      showNewExerciseDialog: false,
      showEditExerciseDialog: false,
      newExerciseDialogKey: 1,
      editExerciseDialogKey: 1,
      sequenceCopyExercisesData: {
        exercises: [],
      },
      newSequenceData: {
        days: [],
      },
      exerciseDefaultParams: {
        pageSize: 100,
        folder: 'All',
      },
      newExerciseDay: null,
      deleteExercisePopup: false,
      exerciseToDelete: null,
      exerciseToEdit: false,
      dialogType: 0,
      imageSrc: false,
      imageName: false,
      dialogFile: null,
      dialogFileMlUrl: null,
      exerciseResource: null,
      exerciseResourceAdditional: null,
      showExercisePreviewDialog: false,
      quickchangesQueue: [],
      quickEditPending: false,
      quickEditProgress: false,
      quickEditProgressTimeout: false,
      quickEditProgressInterval: false,
      quickEditProgressValue: 0,
      infoDialog: false,
    }
  },
  mounted() {
    this.getSequenceDefinition({ id: this.sequenceId }).then(() => {
      this.newSequenceData = { ...this.sequence }
    })

    const that = this

    window.addEventListener('beforeunload', async event => {
      const needsToBeConfirmed =
        that?.quickEditPending || that?.quickEditProgress
      if (needsToBeConfirmed) {
        event.returnValue = 'You have unsaved changes that will be lost.'
      }
    })
  },
  computed: {
    ...mapState('exercisesStates', {
      sequence: 'sequenceDefinition',
      sequenceLoading: 'loading',
      exerciseDefinitions: 'exerciseDefinitions',
      exerciseDefinition: 'exerciseDefinition',
      dialogExerciseDefinitions: 'dialogExerciseDefinitions',
      exerciseDefinitionsPagination: 'pagination',
    }),
    ...mapState('resourcesStates', {
      resource: 'resource',
      resourceLoading: 'loading',
    }),
    ...mapGetters('dictionariesStates', ['getMimeTypes']),
    isApple,
    sequenceId() {
      return this.$route.params.id
    },
    loading() {
      return this.sequenceLoading || this.resourceLoading
    },
    exerciseSingleDefinitions() {
      let arr = []
      this.dialogExerciseDefinitions.forEach(element => {
        if (element.type == 'EXERCISE') {
          let obj = {}
          for (const [key, value] of Object.entries(element.exercise)) {
            obj[key] = value
          }
          arr.push(obj)
        }
      })
      return arr
    },
  },
  watch: {
    sequence(val) {
      if (val) {
        this.newSequenceData = cloneDeep(val)
      }
    },
  },
  beforeRouteLeave: async function(to, from, next) {
    let confirm = !this.quickEditPending && !this.quickEditProgress

    if (!confirm) {
      confirm = await window.confirm(
        'You have unsaved changes that will be lost.',
      )
    }

    if (!confirm) {
      next(false)
      return
    }

    this.stopQuickEdit()
    next()
  },
  methods: {
    ...mapActions('exercisesStates', [
      'getSequenceDefinition',
      'getExerciseDefinition',
      'getExerciseDefinitions',
      'changeSequence',
      'deleteSequence',
      'addExerciseToSequenceDefinition',
      'duplicateSequenceExercise',
      'changeExerciseInSequenceDefinition',
      'removeExerciseFromSequenceDefinition',
      'addExerciseDefinition',
      'changeExerciseDefinition',
      'getDialogExerciseDefinitions',
      'removeExercisesFromSequenceDefinition',
      'copySequenceExercises',
    ]),
    ...mapActions('resourcesStates', ['getResource', 'getResourceById']),
    ...mapMutations('resourcesStates', ['setResource']),
    ...mapMutations('exercisesStates', [
      'setExerciseDefinition',
      'setExerciseDefinitions',
    ]),
    prepareDeleteSequence(id) {
      this.deleteSequence(id).then(() => {
        setTimeout(() => {
          this.$router.push({ name: 'Exercises', path: '/sequences/' })
        }, 2000)
      })
    },
    prepareEditSequence(input, val) {
      this.newSequenceData[input] = val
      this.saveSequenceChanges()
    },
    prepareSequencePractices(practices) {
      this.sharePractices({ id: this.newSequenceData.id, practices: practices })
    },
    reloadResource(id) {
      this.getResource({ id: id })
    },
    searchClear() {
      this.getExerciseDefinitions({ params: {} })
    },
    searchInput({ key, value }) {
      this.getExerciseDefinitions({ params: { [key]: value } })
    },
    searchMultiple(list) {
      let query = { ...this.exerciseDefaultParams }
      list.forEach(item => {
        query = { ...query, [item.key]: item.value }
      })
      this.getExerciseDefinitions({ params: query })
    },
    searchRun(search) {
      this.getExerciseDefinitions({ params: { q: search } })
    },
    reloadExerciseDefinitions(data = new URLSearchParams()) {
      const availableFilters = [
        'page',
        'pageSize',
        'sortBy',
        'sortDirection',
        'type',
      ]

      let filters = {}
      data.forEach((value, key) => {
        if (availableFilters.includes(key)) {
          filters[key] = value
        }
      })

      this.getExerciseDefinitions({
        params: { ...filters, folder: 'All' },
      })
    },
    clearResource() {
      this.setResource(null)
    },
    clearExercises() {
      this.setExerciseDefinitions([])
    },
    clearExerciseResource() {
      this.exerciseResource = null
    },
    clearExerciseResourceAdditional() {
      this.exerciseResourceAdditional = null
    },
    prepareNewExercise(day, file) {
      this.newExerciseDay = day
      this.openNewExerciseDialog()
    },
    openNewExerciseDialog() {
      this.showNewExerciseDialog = true
    },
    closeNewExerciseDialog() {
      this.newExerciseDialogKey += 1
      this.clearResource()
      this.clearExercises()
      this.exerciseResource = null
      this.showNewExerciseDialog = false
      this.exerciseToEdit = false
      this.showEditExerciseDialog = false
      this.setExerciseDefinition(null)
    },
    openSequenceCopyExercisesDialog() {
      this.showSequenceCopyExercisesDialog = true
    },
    closeSequenceCopyExercisesDialog() {
      this.showSequenceCopyExercisesDialog = false
    },
    closeDeleteExercisePopup() {
      this.deleteExercisePopup = false
      this.exerciseToDelete = null
    },
    onClickOutside() {
      this.closeDeleteExercisePopup()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.closeDeleteExercisePopup()
    },
    addSequenceExercise(data) {
      const exerciseType = data.type
      if (!this.newSequenceData.days) this.newSequenceData.days = []
      let ind = this.newSequenceData.days.find(item => item.day == data.day)
      if (ind) ind.items.push(data)
      else {
        let exercises = []
        exercises.push(data)
        this.newSequenceData.days.push({ day: data.day, items: exercises })
      }
      this.addExerciseDefinition({
        data: data,
      }).then(() => {
        this.addExerciseToSequenceDefinition({
          sequenceId: this.sequence.id,
          exerciseId: this.exerciseDefinition.id,
          type: exerciseType,
          day: this.newExerciseDay,
        }).then(() => {
          this.closeNewExerciseDialog()
        })
      })
    },
    addSequenceExistingExercise(exerciseId, type, day) {
      this.addExerciseToSequenceDefinition({
        sequenceId: this.sequence.id,
        type: type,
        exerciseId: exerciseId,
        day: day,
      }).then(() => {
        this.closeNewExerciseDialog()
      })
    },
    editSequenceExercise(data) {
      let ind = this.newSequenceData.days.find(item => item.day == data.day)
      let pos = false
      if (ind)
        pos = ind.exercises.findIndex(
          item => item.exercise == this.exerciseToEdit.exercise,
        )
      if (pos >= 0) {
        ind.exercises.splice(pos, 1)
        ind.exercises.push(data)
        this.saveSequenceChanges()
      }
    },
    saveSequenceChanges() {
      this.changeSequence({
        id: this.newSequenceData.id,
        data: this.newSequenceData,
      })
    },
    deleteSequenceExercise() {
      let ind = this.newSequenceData.days.find(
        item => item.day == this.exerciseToDelete.day,
      )
      if (ind && ind.exercises) {
        let pos = ind.exercises.findIndex(
          item => item.exercise == this.exerciseToDelete.exercise,
        )
        ind.exercises.splice(pos, 1)
      }
      this.saveSequenceChanges()
      this.closeDeleteMessagePopup()
    },
    prepareCopySequenceExercises(exercises = []) {
      if (exercises.length) {
        this.sequenceCopyExercisesData.exercises = exercises
        this.openSequenceCopyExercisesDialog()
      }
    },
    submitCopySequenceExercises({ items = [], start, repetitions, interval }) {
      this.copySequenceExercises({
        items: items.map(el => el.id),
        start,
        repetitions,
        interval,
        sequenceId: this.sequence.id,
      }).then(() => {
        this.closeSequenceCopyExercisesDialog()
        this.$refs.sequenceExercisesContainer.deselectAllElements()
      })
    },
    pasteSquenceExercises({ start = 0, items = [] }) {
      this.copySequenceExercises({
        items: items.map(el => el.id),
        start,
        repetitions: 1,
        interval: 1,
        sequenceId: this.sequence.id,
      })
    },
    copySequenceExercise(data, day) {
      let ind = this.newSequenceData.days.find(item => item.day == day)
      if (ind && ind.exercises) {
        let item = ind.exercises.find(item => item.exercise == data.exercise)
        ind.exercises.push(item)
      }
      this.saveSequenceChanges()
      this.closeDeleteExercisePopup()
    },
    async prepareEditExercise(data) {
      await this.getExerciseDefinition({ id: data.id, type: data.type })
      this.showEditExerciseDialog = true
    },
    async openCreateNewExerciseDialog() {
      await this.setExerciseDefinition(null)
      this.showEditExerciseDialog = true
    },
    saveEditExercise(data) {
      let newData = { id: data.id, type: data.type, data: data }
      if (data.media?.mlSourceResourceId) {
        newData.data['media'] = {
          mlSourceResourceId: data.media.mlSourceResourceId,
        }
      }
      if (data.additionalMedia?.mlSourceResourceId) {
        newData.data['additionalMedia'] = {
          mlSourceResourceId: data.additionalMedia.mlSourceResourceId,
        }
      }
      if (data.mobileStaticMedia) {
        newData.data['mobileStaticMedia'] = data.mobileStaticMedia
      }
      this.changeExerciseDefinition({
        data: data,
      }).then(() => {
        this.getSequenceDefinition({ id: this.sequenceId }).then(() => {
          this.newSequenceData = { ...this.sequence }
        })
      })
    },
    prepareQuickEdit(exercise, data) {
      this.quickEditPending = true
      this.quickEditProgress = true
      this.quickEditProgressStart()
      let changedData = {}
      switch (exercise.type) {
        case 'EXERCISE':
          changedData = { ...exercise.exercise }
          break
        case 'ACTIVITY':
          changedData = { ...exercise.activity }
          break
        case 'SERIES':
          changedData = { ...exercise.series }
          break
        default:
          break
      }
      changedData.type = exercise.type

      let ind = this.quickchangesQueue.findIndex(item => {
        if (item.id == changedData.id && item.type == changedData.type)
          return item
      })
      if (ind > -1) this.quickchangesQueue[ind][data.key] = data.value
      else {
        changedData[data.key] = data.value
        this.quickchangesQueue.push(changedData)
      }

      debounce(
        () => {
          this.quickchangesQueue.forEach(item => {
            this.saveEditExercise(item, null)
          })
          this.quickchangesQueue = []
          this.quickEditPending = false
        },
        2000,
        'QuickEdit',
      )
    },
    stopQuickEdit() {
      clearTimeout(this.quickEditProgressTimeout)
      clearInterval(this.quickEditProgressInterval)
      this.quickEditProgressValue = 0
      this.quickchangesQueue = []
      this.quickEditPending = false
      this.quickEditProgress = false
      debounce(() => {}, 2000, 'QuickEdit', 'cancel')
    },
    prepareSeriesQuickEdit(data) {
      this.prepareQuickEdit({ type: 'EXERCISE', exercise: data.exercise }, data)
    },
    quickEditProgressStart() {
      clearTimeout(this.quickEditProgressTimeout)

      if (this.quickEditProgressInterval)
        clearInterval(this.quickEditProgressInterval)
      this.quickEditProgressValue = 0
      this.quickEditProgressInterval = setInterval(() => {
        if (this.quickEditProgressValue > 100) {
          clearInterval(this.quickEditProgressInterval)
          return
        }
        this.quickEditProgressValue += 25
      }, 250)
      if (this.quickEditProgressTimeout)
        clearTimeout(this.quickEditProgressTimeout)
      this.quickEditProgressTimeout = setTimeout(() => {
        this.quickEditProgress = false
        this.quickEditProgressValue = 0
        clearInterval(this.quickEditProgressInterval)
      }, 3000)
    },
    prepareExerciseResource(id, resourceType) {
      if (!id) {
        if (resourceType == 'additionalMedia') {
          this.exerciseResourceAdditional = null
        } else {
          this.exerciseResource = null
        }
      } else {
        this.getResourceById({ resourceId: id })
          .then(response => {
            const { data } = response
            let type = 2
            switch (data.fileTypeId) {
              case 18:
                type = 2
                break
              case 19:
                type = 4
                break
              default:
                type = 2
                break
            }
            if (resourceType == 'additionalMedia') {
              if (data.fileUrl) {
                this.exerciseResourceAdditional = {
                  id: id,
                  previewUrl: data.previewUrl,
                  thumbnailUrl: data.thumbnailUrl,
                  name: data.name,
                  title: data.title,
                  dialogFileMlUrl: data.fileUrl,
                  fileUrl: data.fileUrl,
                }
              } else {
                this.exerciseResourceAdditional = null
              }
            } else {
              if (data.fileUrl) {
                this.exerciseResource = {
                  id: id,
                  previewUrl: data.previewUrl,
                  thumbnailUrl: data.thumbnailUrl,
                  name: data.name,
                  title: data.title,
                  dialogFileMlUrl: data.fileUrl,
                  fileUrl: data.fileUrl,
                }
              } else {
                this.exerciseResourceAdditional = null
              }
            }
          })
          .catch(error => {
            console.log(error)
          })
      }
    },
    openInfoDialog() {
      this.infoDialog = true
    },
    closeInfoDialog() {
      this.infoDialog = false
    },
    prepareMoveExercise(exerciseId, data) {
      this.changeExerciseInSequenceDefinition({
        itemId: exerciseId,
        sequenceId: this.sequence.id,
        data: data,
      })
    },
    prepareRemoveExercises(exercisesIds = []) {
      this.removeExercisesFromSequenceDefinition({
        items: exercisesIds,
        sequenceId: this.sequence.id,
      }).then(() => {
        this.$refs.sequenceExercisesContainer.deselectAllElements()

        this.getSequenceDefinition({ id: this.sequenceId })
      })
    },
    prepareRemoveExercise(exerciseId) {
      this.removeExerciseFromSequenceDefinition({
        itemId: exerciseId,
        sequenceId: this.sequence.id,
      })
    },
    prepareDuplicateExercise(itemId, type) {
      this.duplicateSequenceExercise({
        sequenceId: this.sequence.id,
        itemId: itemId,
        type: type,
      })
    },
    prepareDeleteExercise(data, day) {
      this.exerciseToDelete = data
      this.exerciseToDelete.day = day
      this.deleteExercisePopup = true
    },
    showBiggerImage({ url, file, name, mlResourceUrl = null, type = 1 }) {
      this.imageSrc = url
      this.dialogFile = file
      this.imageName = name
      this.dialogFileMlUrl = mlResourceUrl
      this.dialogType = type
    },
    async reloadImageResource(id) {
      // await this.getResource({ id: id })
      // this.imageSrc = this.resource.fileUrl
      // this.imageName = this.resource.name
    },
    async reloadExerciseResource(id, type) {
      // if (type == 'additionalMedia') this.exerciseResourceAdditional = null
      // else this.exerciseResource = null
      // await this.prepareExerciseResource(id, type)
    },
    closeMediaDialog() {
      this.dialogType = 0
      this.imageName = null
      this.currentFile = null
      this.imageSrc = null
      this.dialogFileMlUrl = null
      this.dialogType = null
    },
    async exerciseGetPreview(exercise, type) {
      const id = parseInt(exercise.split('__')[1])
      await this.getExerciseDefinition({ id: id, type: type })
      this.showExercisePreviewDialog = true
    },
    openMediaPreview(media) {
      this.showBiggerImage({
        url: media.mediaUrl,
        file: media.mediaUrl,
        name: media.filename,
        mlResourceUrl: null,
        type: media.fileTypeId == 19 ? 3 : 1,
      })
    },
    loadDialogExerciseDefinitions(params) {
      let defaultParams = {
        type: 'EXERCISE',
        page: 0,
        pageSize: 50,
        sortBy: 'creationDate',
        folder: 'All',
      }
      this.getDialogExerciseDefinitions({
        params: { ...defaultParams, ...params },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.sequence {
  &__quick-edit-progress {
    span {
      color: $bh-blue;
      font-weight: bold;
    }
    .v-progress-circular__overlay,
    .v-progress-circular__underlay {
      transition: 0.25s linear !important;
    }
  }
}
</style>
